import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { customerInvoicesApi, customerPaymentsApi } from '../lib/api';
import PaymentModal from '../components/PaymentModal';
import BankTransferPanel from '../components/BankTransferPanel';
import { ArrowLeft, CreditCard, CheckCircle, Clock, AlertCircle, XCircle, FileText, Landmark } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  periodStart?: string;
  periodEnd?: string;
  product?: { id: string; domain: string; type: string };
}

interface Payment {
  id: string;
  amount: number;
  status: string;
  gateway: string;
  paymentMethodLast4?: string;
  createdAt: string;
}

interface Invoice {
  id: string;
  number: string;
  status: string;
  total: number;
  subtotal: number;
  taxAmount: number;
  paidAmount: number;
  currency: string;
  issueDate: string;
  dueDate: string;
  notes?: string;
  items: InvoiceItem[];
  payments: Payment[];
}

const statusConfig = {
  PAID: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
  PENDING: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  SENT: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100' },
  OVERDUE: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100' },
  CANCELLED: { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-100' },
  REFUNDED: { icon: XCircle, color: 'text-purple-600', bg: 'bg-purple-100' },
  DRAFT: { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
};

export default function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  /**
   * Which ways of paying are actually switched on.
   *
   * The Pay button used to assume a card: with only transfers enabled it
   * opened Stripe, which answered that card payments are unavailable — an
   * apology where the instructions should have been. Undefined until the
   * providers load, so the button does not flicker between the two labels.
   */
  const [cardEnabled, setCardEnabled] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  /**
   * `?pay=true` arrives from the dashboard, the invoice list and the order
   * flow. It used to open the card form on sight, so with only transfers
   * enabled every one of those links led to "card payments are not available".
   * The intent behind the link is "pay this now", which without a card means
   * the transfer panel.
   */
  useEffect(() => {
    if (cardEnabled === undefined || searchParams.get('pay') !== 'true') return;
    if (cardEnabled) {
      setShowPayment(true);
    } else {
      document.getElementById('bank-transfer')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [cardEnabled, searchParams]);

  useEffect(() => {
    customerPaymentsApi
      .getProviders()
      // A provider that confirms its own payments is one the browser can take
      // money through; a transfer is settled by a human reading a receipt.
      .then((res) => setCardEnabled((res.data || []).some((p) => p.supportsAutoConfirm)))
      // Failing to ask is not proof there is no card, but offering a button
      // that apologises is worse than offering the transfer we know works.
      .catch(() => setCardEnabled(false));
  }, []);

  async function loadInvoice() {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await customerInvoicesApi.get(id);
      setInvoice(response.data as Invoice);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  function formatCurrency(amount: number, currency: string) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="space-y-4">
        <Link to="/invoices" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" />
          {t('invoices.backToInvoices')}
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error || 'Invoice not found'}</span>
          </div>
        </div>
      </div>
    );
  }

  const config = (statusConfig[invoice.status as keyof typeof statusConfig] ?? statusConfig.DRAFT)!;
  const StatusIcon = config.icon;
  const amountDue = invoice.total - invoice.paidAmount;
  const canPay = ['PENDING', 'SENT', 'OVERDUE'].includes(invoice.status) && amountDue > 0;

  return (
    <div className="space-y-6">
      <Link to="/invoices" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" />
        {t('invoices.backToInvoices')}
      </Link>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoice {invoice.number}</h1>
            <p className="text-gray-600 mt-1">Issued on {formatDate(invoice.issueDate)}</p>
          </div>
          <div className="text-right">
            <span
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium',
                config.bg,
                config.color
              )}
            >
              <StatusIcon className="h-4 w-4" />
              {invoice.status}
            </span>
            {canPay && cardEnabled !== undefined && (
              cardEnabled ? (
                <button
                  onClick={() => setShowPayment(true)}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium"
                >
                  <CreditCard className="h-5 w-5" />
                  Pay {formatCurrency(amountDue, invoice.currency)}
                </button>
              ) : (
                <a
                  href="#bank-transfer"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium"
                >
                  <Landmark className="h-5 w-5" />
                  {t('invoices.payByTransfer')}
                </a>
              )
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500">{t('invoices.issueDate')}</p>
            <p className="font-medium">{formatDate(invoice.issueDate)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('invoices.dueDateLbl')}</p>
            <p className="font-medium">{formatDate(invoice.dueDate)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('invoices.total')}</p>
            <p className="font-medium">{formatCurrency(invoice.total, invoice.currency)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('invoices.amountDue')}</p>
            <p className="font-bold text-lg">{formatCurrency(amountDue, invoice.currency)}</p>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">{t('invoices.lineItems')}</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t('invoices.description')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Qty
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                {t('invoices.unitPrice')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                {t('invoices.total')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {invoice.items.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4">
                  <p className="font-medium">{item.description}</p>
                  {item.periodStart && item.periodEnd && (
                    <p className="text-sm text-gray-500">
                      {formatDate(item.periodStart)} - {formatDate(item.periodEnd)}
                    </p>
                  )}
                </td>
                <td className="px-6 py-4 text-right">{item.quantity}</td>
                <td className="px-6 py-4 text-right">
                  {formatCurrency(item.unitPrice, invoice.currency)}
                </td>
                <td className="px-6 py-4 text-right font-medium">
                  {formatCurrency(item.total, invoice.currency)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td colSpan={3} className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                {t('invoices.subtotal')}
              </td>
              <td className="px-6 py-3 text-right font-medium">
                {formatCurrency(invoice.subtotal, invoice.currency)}
              </td>
            </tr>
            {invoice.taxAmount > 0 && (
              <tr>
                <td colSpan={3} className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  Tax
                </td>
                <td className="px-6 py-3 text-right font-medium">
                  {formatCurrency(invoice.taxAmount, invoice.currency)}
                </td>
              </tr>
            )}
            <tr className="bg-gray-100">
              <td colSpan={3} className="px-6 py-3 text-right font-semibold">
                {t('invoices.total')}
              </td>
              <td className="px-6 py-3 text-right font-bold text-lg">
                {formatCurrency(invoice.total, invoice.currency)}
              </td>
            </tr>
            {invoice.paidAmount > 0 && (
              <>
                <tr>
                  <td colSpan={3} className="px-6 py-3 text-right text-sm font-medium text-green-600">
                    Paid
                  </td>
                  <td className="px-6 py-3 text-right font-medium text-green-600">
                    -{formatCurrency(invoice.paidAmount, invoice.currency)}
                  </td>
                </tr>
                <tr className="bg-brand-50">
                  <td colSpan={3} className="px-6 py-3 text-right font-semibold text-brand-900">
                    {t('invoices.amountDue')}
                  </td>
                  <td className="px-6 py-3 text-right font-bold text-lg text-brand-900">
                    {formatCurrency(amountDue, invoice.currency)}
                  </td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>

      {/* Payment History */}
      {invoice.payments.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">{t('invoices.paymentHistory')}</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.method')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.statusLbl')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.amountLbl')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoice.payments.map((payment) => (
                <tr key={payment.id}>
                  <td className="px-6 py-4">{formatDate(payment.createdAt)}</td>
                  <td className="px-6 py-4">
                    {payment.gateway}
                    {payment.paymentMethodLast4 && ` •••• ${payment.paymentMethodLast4}`}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={clsx(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        payment.status === 'COMPLETED'
                          ? 'bg-green-100 text-green-700'
                          : payment.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      )}
                    >
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-medium">
                    {formatCurrency(payment.amount, invoice.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bank transfer (SPEI) — renders nothing unless the provider enables it */}
      {canPay && (
        <div id="bank-transfer" className="scroll-mt-6">
        <BankTransferPanel
          invoiceId={invoice.id}
          invoiceNumber={invoice.number}
          amountDue={amountDue}
          currency={invoice.currency}
          onSubmitted={loadInvoice}
        />
        </div>
      )}

      {/* Notes */}
      {invoice.notes && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-2">{t('invoices.notes')}</h2>
          <p className="text-gray-600 whitespace-pre-wrap">{invoice.notes}</p>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && (
        <PaymentModal
          invoiceId={invoice.id}
          amount={amountDue}
          currency={invoice.currency}
          onClose={() => setShowPayment(false)}
          onSuccess={() => {
            setShowPayment(false);
            loadInvoice();
          }}
        />
      )}
    </div>
  );
}
